"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageSquare, ThumbsUp, CheckCircle2, XCircle, Lightbulb,
  ChevronDown, ChevronUp, Send, User, Loader2, Trash2, ShieldAlert, Clock, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTurnstile } from "@/hooks/useTurnstile";

type RepairResult = "success" | "failed" | "tip";

const RESULT_CONFIG: Record<RepairResult, { label: string; icon: React.ElementType; color: string; bg: string; border: string }> = {
  success: { label: "修理成功", icon: CheckCircle2, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  failed:  { label: "修理失敗", icon: XCircle,      color: "text-red-400",     bg: "bg-red-500/10",     border: "border-red-500/30"     },
  tip:     { label: "アドバイス", icon: Lightbulb,  color: "text-amber-400",   bg: "bg-amber-500/10",   border: "border-amber-500/30"   },
};

const REPAIR_TYPES = [
  "画面（ディスプレイ）交換", "バッテリー交換", "充電ポート交換",
  "カメラ交換", "スピーカー交換", "ボタン交換", "水没修理", "その他",
];

const LS_KEY = "comment_own_keys";

interface OwnKeyEntry {
  key: string;
  modelName: string;
}

function loadOwnKeys(): Record<number, OwnKeyEntry> {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveOwnKey(commentId: number, key: string, modelName: string) {
  try {
    const current = loadOwnKeys();
    current[commentId] = { key, modelName };
    localStorage.setItem(LS_KEY, JSON.stringify(current));
  } catch {}
}

interface Comment {
  id: number;
  modelName: string;
  authorName: string;
  repairResult: RepairResult;
  repairType: string;
  body: string;
  helpfulCount: number;
  status: "pending" | "approved" | "rejected";
  createdAt: string | Date;
}

function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString("ja-JP", { year: "numeric", month: "long", day: "numeric" });
}

// ---------------------------------------------------------------------------
// 投稿成功ポップアップ
// ---------------------------------------------------------------------------
interface SuccessPopupProps {
  deletionKey: string;
  onClose: () => void;
}

function SuccessPopup({ deletionKey, onClose }: SuccessPopupProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(deletionKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative w-full max-w-md rounded-xl border border-border/60 shadow-2xl p-6 space-y-4"
        style={{ background: "oklch(0.14 0.015 260)" }}
      >
        <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded text-muted-foreground/50 hover:text-foreground transition-colors">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 text-emerald-400">
          <CheckCircle2 className="w-5 h-5 shrink-0" />
          <h3 className="font-semibold text-sm">投稿ありがとうございます</h3>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          運営者がコメントをチェックして、問題なければ掲載させていただきます。
        </p>
        <div className="border border-amber-500/30 bg-amber-500/10 rounded-lg p-4 space-y-2">
          <p className="text-xs text-amber-300 font-mono font-semibold">削除キー（保管してください）</p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            コメントを削除する場合、削除キーを求める場合があります。こちらは大切に保管してください。
          </p>
          <div className="flex items-center gap-2 mt-2">
            <code className="flex-1 px-3 py-1.5 rounded bg-background/60 border border-border/40 text-xs font-mono break-all text-foreground/80">
              {deletionKey}
            </code>
            <button
              onClick={handleCopy}
              className="shrink-0 px-3 py-1.5 rounded border border-border/40 text-xs font-mono text-muted-foreground hover:text-foreground hover:border-border/80 transition-colors"
            >
              {copied ? "コピー済" : "コピー"}
            </button>
          </div>
        </div>
        <Button onClick={onClose} className="w-full font-mono text-sm">閉じる</Button>
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 削除ダイアログ
// ---------------------------------------------------------------------------
type DeleteDialogMode =
  | { type: "admin"; commentId: number }
  | { type: "own"; commentId: number; key: string }
  | { type: "key"; commentId: number }
  | { type: "request"; commentId: number };

interface DeleteDialogProps {
  mode: DeleteDialogMode;
  onClose: () => void;
  onDeleted: () => void;
}

function DeleteDialog({ mode, onClose, onDeleted }: DeleteDialogProps) {
  const [keyInput, setKeyInput]     = useState("");
  const [reason, setReason]         = useState("");
  const [isLoading, setIsLoading]   = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [requestSent, setRequestSent] = useState(false);

  const handleDelete = async () => {
    setIsLoading(true);
    setError(null);
    try {
      let body: Record<string, unknown> = {};
      if (mode.type === "key") body = { deletionKey: keyInput };
      else if (mode.type === "own") body = { deletionKey: mode.key };

      const res = await fetch(`/api/comments/${mode.commentId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "削除に失敗しました");
      }
      onDeleted();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "削除に失敗しました");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequest = async () => {
    if (!reason.trim()) { setError("削除理由を入力してください"); return; }
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/comments/request-delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ commentId: mode.commentId, reason: reason.trim() }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "申請に失敗しました");
      }
      setRequestSent(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "申請に失敗しました");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative w-full max-w-md rounded-xl border border-border/60 shadow-2xl p-6 space-y-4"
        style={{ background: "oklch(0.14 0.015 260)" }}
      >
        <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded text-muted-foreground/50 hover:text-foreground transition-colors">
          <X className="w-4 h-4" />
        </button>

        {requestSent ? (
          <>
            <div className="flex items-center gap-2 text-emerald-400">
              <CheckCircle2 className="w-5 h-5" />
              <h3 className="font-semibold text-sm">削除依頼を送信しました</h3>
            </div>
            <p className="text-sm text-muted-foreground">運営者が内容を確認の上、対応いたします。</p>
            <Button onClick={onClose} className="w-full font-mono text-sm">閉じる</Button>
          </>
        ) : mode.type === "request" ? (
          <>
            <h3 className="font-semibold text-sm text-foreground">削除依頼</h3>
            <p className="text-xs text-muted-foreground">
              削除キーをお持ちでない場合は、削除依頼を送ることができます。運営者が確認の上、対応します。
            </p>
            <div>
              <label className="text-xs font-mono text-muted-foreground mb-1.5 block">削除理由 <span className="text-red-400">*</span></label>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                rows={4}
                placeholder="削除を希望する理由を入力してください..."
                className="w-full px-3 py-2 text-sm rounded-md border border-border/60 bg-background/50 focus:outline-none focus:border-primary/60 transition-colors resize-none"
              />
            </div>
            {error && <p className="text-xs text-red-400 font-mono">{error}</p>}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={onClose} className="font-mono text-sm">キャンセル</Button>
              <Button onClick={handleRequest} disabled={isLoading || !reason.trim()} className="font-mono text-sm">
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "依頼を送信"}
              </Button>
            </div>
          </>
        ) : mode.type === "key" ? (
          <>
            <h3 className="font-semibold text-sm text-foreground">削除キーを入力</h3>
            <div>
              <label className="text-xs font-mono text-muted-foreground mb-1.5 block">削除キー</label>
              <input
                type="text"
                value={keyInput}
                onChange={e => setKeyInput(e.target.value)}
                placeholder="投稿時に発行された削除キー"
                className="w-full px-3 py-2 text-sm rounded-md border border-border/60 bg-background/50 focus:outline-none focus:border-primary/60 transition-colors font-mono"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              削除キーをお持ちでない場合は{" "}
              <button
                className="underline text-primary/80 hover:text-primary transition-colors"
                onClick={() => {
                  /* switch to request mode - handled by parent */
                }}
              >こちら</button>
            </p>
            {error && <p className="text-xs text-red-400 font-mono">{error}</p>}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={onClose} className="font-mono text-sm">キャンセル</Button>
              <Button
                onClick={handleDelete}
                disabled={isLoading || !keyInput.trim()}
                className="font-mono text-sm bg-red-600 hover:bg-red-700"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "削除する"}
              </Button>
            </div>
          </>
        ) : (
          <>
            <h3 className="font-semibold text-sm text-foreground">コメントを削除しますか？</h3>
            <p className="text-sm text-muted-foreground">この操作は取り消せません。</p>
            {error && <p className="text-xs text-red-400 font-mono">{error}</p>}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={onClose} className="font-mono text-sm">キャンセル</Button>
              <Button onClick={handleDelete} disabled={isLoading} className="font-mono text-sm bg-red-600 hover:bg-red-700">
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "削除する"}
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 削除ボタン（モードに応じたダイアログを開く）
// ---------------------------------------------------------------------------
interface DeleteButtonProps {
  comment: Comment;
  isAdmin: boolean;
  ownKeys: Record<number, OwnKeyEntry>;
  onDeleted: () => void;
}

function DeleteButton({ comment, isAdmin, ownKeys, onDeleted }: DeleteButtonProps) {
  const [dialogMode, setDialogMode] = useState<DeleteDialogMode | null>(null);

  const handleClick = () => {
    if (isAdmin) {
      setDialogMode({ type: "admin", commentId: comment.id });
    } else if (ownKeys[comment.id]) {
      setDialogMode({ type: "own", commentId: comment.id, key: ownKeys[comment.id].key });
    } else {
      // キーなし → キー入力 or 依頼
      setDialogMode({ type: "key", commentId: comment.id });
    }
  };

  return (
    <>
      <button
        onClick={handleClick}
        className="p-1 rounded text-muted-foreground/40 hover:text-red-400 hover:bg-red-500/10 transition-colors"
        title="削除"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>

      {dialogMode && (
        dialogMode.type === "key" ? (
          <KeyOrRequestDialog
            commentId={comment.id}
            onClose={() => setDialogMode(null)}
            onDeleted={() => { setDialogMode(null); onDeleted(); }}
          />
        ) : (
          <DeleteDialog
            mode={dialogMode}
            onClose={() => setDialogMode(null)}
            onDeleted={() => { setDialogMode(null); onDeleted(); }}
          />
        )
      )}
    </>
  );
}

// キー入力 or 依頼を一体化したダイアログ
function KeyOrRequestDialog({ commentId, onClose, onDeleted }: { commentId: number; onClose: () => void; onDeleted: () => void }) {
  const [tab, setTab]             = useState<"key" | "request">("key");
  const [keyInput, setKeyInput]   = useState("");
  const [reason, setReason]       = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [requestSent, setRequestSent] = useState(false);

  const handleDelete = async () => {
    if (!keyInput.trim()) { setError("削除キーを入力してください"); return; }
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/comments/${commentId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deletionKey: keyInput }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "削除に失敗しました");
      }
      onDeleted();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "削除に失敗しました");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequest = async () => {
    if (!reason.trim()) { setError("削除理由を入力してください"); return; }
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/comments/request-delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ commentId, reason: reason.trim() }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "申請に失敗しました");
      }
      setRequestSent(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "申請に失敗しました");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative w-full max-w-md rounded-xl border border-border/60 shadow-2xl p-6 space-y-4"
        style={{ background: "oklch(0.14 0.015 260)" }}
      >
        <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded text-muted-foreground/50 hover:text-foreground transition-colors">
          <X className="w-4 h-4" />
        </button>

        {requestSent ? (
          <>
            <div className="flex items-center gap-2 text-emerald-400">
              <CheckCircle2 className="w-5 h-5" />
              <h3 className="font-semibold text-sm">削除依頼を送信しました</h3>
            </div>
            <p className="text-sm text-muted-foreground">運営者が内容を確認の上、対応いたします。</p>
            <Button onClick={onClose} className="w-full font-mono text-sm">閉じる</Button>
          </>
        ) : (
          <>
            <h3 className="font-semibold text-sm text-foreground">コメントの削除</h3>

            {/* タブ切り替え */}
            <div className="flex gap-1 border border-border/40 rounded-lg p-1">
              <button
                onClick={() => { setTab("key"); setError(null); }}
                className={`flex-1 text-xs font-mono py-1.5 rounded-md transition-colors ${tab === "key" ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground"}`}
              >
                削除キーで削除
              </button>
              <button
                onClick={() => { setTab("request"); setError(null); }}
                className={`flex-1 text-xs font-mono py-1.5 rounded-md transition-colors ${tab === "request" ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground"}`}
              >
                削除を依頼する
              </button>
            </div>

            {tab === "key" ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-mono text-muted-foreground mb-1.5 block">削除キー</label>
                  <input
                    type="text"
                    value={keyInput}
                    onChange={e => setKeyInput(e.target.value)}
                    placeholder="投稿時に発行された削除キー"
                    className="w-full px-3 py-2 text-sm rounded-md border border-border/60 bg-background/50 focus:outline-none focus:border-primary/60 transition-colors font-mono"
                  />
                </div>
                {error && <p className="text-xs text-red-400 font-mono">{error}</p>}
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={onClose} className="font-mono text-sm">キャンセル</Button>
                  <Button onClick={handleDelete} disabled={isLoading || !keyInput.trim()} className="font-mono text-sm bg-red-600 hover:bg-red-700">
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "削除する"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  削除キーをお持ちでない場合は、削除依頼を送ることができます。運営者が確認の上、対応します。
                </p>
                <div>
                  <label className="text-xs font-mono text-muted-foreground mb-1.5 block">削除理由 <span className="text-red-400">*</span></label>
                  <textarea
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    rows={4}
                    placeholder="削除を希望する理由を入力してください..."
                    className="w-full px-3 py-2 text-sm rounded-md border border-border/60 bg-background/50 focus:outline-none focus:border-primary/60 transition-colors resize-none"
                  />
                </div>
                {error && <p className="text-xs text-red-400 font-mono">{error}</p>}
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={onClose} className="font-mono text-sm">キャンセル</Button>
                  <Button onClick={handleRequest} disabled={isLoading || !reason.trim()} className="font-mono text-sm">
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "依頼を送信"}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 投稿フォーム（Turnstile を含むため独立コンポーネント化）
// ---------------------------------------------------------------------------
interface CommentFormProps {
  modelName: string;
  onSuccess: (deletionKey: string, commentId: number) => void;
}

function CommentForm({ modelName, onSuccess }: CommentFormProps) {
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? "";
  const { containerRef, token: turnstileToken, error: turnstileError, reset: resetTurnstile } = useTurnstile({ siteKey });

  const [authorName, setAuthorName]     = useState("");
  const [repairResult, setRepairResult] = useState<RepairResult>("success");
  const [repairType, setRepairType]     = useState(REPAIR_TYPES[0]);
  const [body, setBody]                 = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError]   = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) { setSubmitError("コメント本文を入力してください"); return; }
    if (siteKey && !turnstileToken) { setSubmitError("Bot 認証を完了してください"); return; }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const res = await fetch("/api/comments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          modelName,
          authorName: authorName.trim() || "匿名",
          repairResult,
          repairType,
          body: body.trim(),
          ...(turnstileToken ? { turnstileToken } : {}),
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "投稿に失敗しました");
      }
      const data = await res.json();
      setBody("");
      setAuthorName("");
      setRepairResult("success");
      setRepairType(REPAIR_TYPES[0]);
      resetTurnstile();
      onSuccess(data.deletionKey, data.id);
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : "投稿に失敗しました");
      resetTurnstile();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-5 space-y-4">
      {/* 投稿者名 */}
      <div>
        <label className="text-xs font-mono text-muted-foreground mb-1.5 block">お名前（任意）</label>
        <input
          type="text"
          value={authorName}
          onChange={e => setAuthorName(e.target.value)}
          placeholder="匿名"
          maxLength={64}
          className="w-full px-3 py-2 text-sm rounded-md border border-border/60 bg-background/50 focus:outline-none focus:border-primary/60 transition-colors"
        />
      </div>

      {/* 修理結果 / 修理種別 */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-mono text-muted-foreground mb-1.5 block">修理結果</label>
          <div className="flex gap-2">
            {(["success", "failed", "tip"] as RepairResult[]).map(r => {
              const cfg = RESULT_CONFIG[r];
              const Icon = cfg.icon;
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRepairResult(r)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-md border text-xs font-mono transition-all ${
                    repairResult === r
                      ? `${cfg.bg} ${cfg.border} ${cfg.color}`
                      : "border-border/40 text-muted-foreground hover:border-border/70"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {cfg.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="text-xs font-mono text-muted-foreground mb-1.5 block">修理種別</label>
          <select
            value={repairType}
            onChange={e => setRepairType(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-md border border-border/60 bg-background/50 focus:outline-none focus:border-primary/60 transition-colors"
          >
            {REPAIR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {/* 本文 */}
      <div>
        <label className="text-xs font-mono text-muted-foreground mb-1.5 block">
          コメント本文 <span className="text-red-400">*</span>
        </label>
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder="修理の体験談やアドバイスを共有してください..."
          rows={4}
          required
          className="w-full px-3 py-2 text-sm rounded-md border border-border/60 bg-background/50 focus:outline-none focus:border-primary/60 transition-colors resize-none"
        />
      </div>

      {/* Turnstile ウィジェット */}
      {siteKey && (
        <div>
          <div ref={containerRef} />
          {turnstileError && (
            <p className="text-xs text-red-400 font-mono mt-1">{turnstileError}</p>
          )}
        </div>
      )}

      {/* エラー */}
      {submitError && <p className="text-xs text-red-400 font-mono">{submitError}</p>}

      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={isSubmitting || !body.trim() || (!!siteKey && !turnstileToken)}
          className="gap-2 text-sm font-mono"
        >
          {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          投稿する
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// メインコンポーネント
// ---------------------------------------------------------------------------
interface CommentSectionProps {
  modelName: string;
}

export function CommentSectionClient({ modelName }: CommentSectionProps) {
  const [comments, setComments]       = useState<Comment[]>([]);
  const [isLoading, setIsLoading]     = useState(true);
  const [showForm, setShowForm]       = useState(false);
  const [helpfulClicked, setHelpfulClicked] = useState<Set<number>>(new Set());
  const [isAdmin, setIsAdmin]         = useState(false);
  const [ownKeys, setOwnKeys]         = useState<Record<number, OwnKeyEntry>>({});
  const [successPopup, setSuccessPopup] = useState<{ key: string } | null>(null);
  const ownKeysRef = useRef<Record<number, OwnKeyEntry>>({});

  // localStorage から自分のキーを読み込む
  useEffect(() => {
    const keys = loadOwnKeys();
    ownKeysRef.current = keys;
    setOwnKeys(keys);
  }, []);

  const fetchComments = useCallback(async () => {
    const ownIds = Object.keys(ownKeysRef.current).map(Number);
    const ownIdsParam = ownIds.length > 0 ? `&ownIds=${ownIds.join(",")}` : "";
    try {
      const res = await fetch(`/api/comments?modelName=${encodeURIComponent(modelName)}${ownIdsParam}`);
      const data = await res.json();
      setComments(Array.isArray(data) ? data : []);
    } catch {
      setComments([]);
    } finally {
      setIsLoading(false);
    }
  }, [modelName]);

  useEffect(() => {
    fetchComments();
    fetch("/api/admin/auth/me")
      .then(r => r.json())
      .then(data => { if (data?.role === "admin") setIsAdmin(true); })
      .catch(() => {});
  }, [fetchComments]);

  const handleHelpful = async (commentId: number) => {
    if (helpfulClicked.has(commentId)) return;
    try {
      await fetch("/api/comments/helpful", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ commentId }),
      });
      setHelpfulClicked(prev => new Set(prev).add(commentId));
      fetchComments();
    } catch {}
  };

  const handleFormSuccess = (deletionKey: string, commentId: number) => {
    // localStorage に保存
    saveOwnKey(commentId, deletionKey, modelName);
    const newKeys = loadOwnKeys();
    ownKeysRef.current = newKeys;
    setOwnKeys(newKeys);
    setShowForm(false);
    setSuccessPopup({ key: deletionKey });
    fetchComments();
  };

  return (
    <>
      <motion.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.4 }}
        className="mt-8 rounded-lg border border-border/60 overflow-hidden"
        style={{ background: "oklch(0.14 0.015 260)" }}
      >
        {/* ヘッダー */}
        <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border/50">
          <MessageSquare className="w-4 h-4 text-primary" />
          <h2 className="font-semibold text-sm tracking-wide" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            修理体験・アドバイス
          </h2>
          {isAdmin && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono bg-amber-500/10 border border-amber-500/30 text-amber-400">
              <ShieldAlert className="w-3 h-3" />管理者モード
            </span>
          )}
          <span className="ml-auto text-xs font-mono text-muted-foreground">{comments.length} 件</span>
          <Button
            size="sm" variant="outline"
            className="ml-2 gap-1.5 text-xs font-mono border-primary/40 text-primary hover:bg-primary/10"
            onClick={() => setShowForm(v => !v)}
          >
            {showForm ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {showForm ? "閉じる" : "投稿する"}
          </Button>
        </div>

        {/* 投稿フォーム */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden border-b border-border/40"
            >
              <CommentForm modelName={modelName} onSuccess={handleFormSuccess} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* コメント一覧 */}
        <div className="divide-y divide-border/30">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {!isLoading && comments.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground font-mono">
              まだコメントはありません。最初の投稿者になりましょう！
            </div>
          )}
          {comments.map(comment => {
            const cfg = RESULT_CONFIG[comment.repairResult] ?? RESULT_CONFIG.tip;
            const Icon = cfg.icon;
            const alreadyHelpful = helpfulClicked.has(comment.id);
            const isPending = comment.status === "pending";
            const isOwn = Boolean(ownKeys[comment.id]);

            return (
              <motion.div
                key={comment.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`px-5 py-4 ${isPending ? "opacity-80" : ""}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono border ${cfg.bg} ${cfg.border} ${cfg.color}`}>
                      <Icon className="w-3 h-3" />
                      {cfg.label}
                    </span>
                    <span className="text-xs text-muted-foreground/60 border border-border/40 px-2 py-0.5 rounded font-mono">
                      {comment.repairType}
                    </span>
                    {isPending && isOwn && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono bg-blue-500/10 border border-blue-500/30 text-blue-400">
                        <Clock className="w-3 h-3" />審査中
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <DeleteButton
                      comment={comment}
                      isAdmin={isAdmin}
                      ownKeys={ownKeys}
                      onDeleted={() => fetchComments()}
                    />
                    <button
                      onClick={() => handleHelpful(comment.id)}
                      disabled={alreadyHelpful || isPending}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-mono transition-all ${
                        alreadyHelpful
                          ? "bg-primary/15 text-primary border border-primary/30"
                          : isPending
                          ? "border border-border/20 text-muted-foreground/30 cursor-default"
                          : "border border-border/40 text-muted-foreground hover:border-primary/40 hover:text-primary"
                      }`}
                    >
                      <ThumbsUp className="w-3 h-3" />
                      {comment.helpfulCount}
                    </button>
                  </div>
                </div>
                <p className="text-sm text-foreground/85 leading-relaxed mb-2">{comment.body}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground/50 font-mono">
                  <User className="w-3 h-3" />
                  <span>{comment.authorName}</span>
                  <span>·</span>
                  <span>{formatDate(comment.createdAt)}</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.section>

      {/* 投稿成功ポップアップ */}
      {successPopup && (
        <SuccessPopup
          deletionKey={successPopup.key}
          onClose={() => setSuccessPopup(null)}
        />
      )}
    </>
  );
}
